/**
 * Market picker: whether it is open, the element it drops down from, and the markets recently picked from it (kept
 * in this browser).
 */
import { createStore, useStore } from '../lib/store';

const KEY = 'recentMarkets';
const MAX_RECENT = 6;

function readRecent(): string[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string').slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}

function saveRecent(recent: string[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(recent));
  } catch {
    // Storage blocked (private window): recents last for this visit only.
  }
}

export const searchStore = createStore<{ open: boolean; anchor: HTMLElement | null; recent: string[] }>({ open: false, anchor: null, recent: readRecent() });

/** Opens the picker under `anchor`; without one (the keyboard shortcuts) it drops from the top bar's search. */
export const openSearch = (anchor: HTMLElement | null = null) => searchStore.set({ open: true, anchor });
export const closeSearch = () => searchStore.set({ open: false, anchor: null });
/** For the button the picker hangs from: a second click closes it. */
export const toggleSearch = (anchor: HTMLElement) => {
  const s = searchStore.get();
  if (s.open && s.anchor === anchor) closeSearch();
  else openSearch(anchor);
};

export function addRecent(symbol: string) {
  const recent = [symbol, ...searchStore.get().recent.filter((s) => s !== symbol)].slice(0, MAX_RECENT);
  searchStore.set({ recent });
  saveRecent(recent);
}

export function clearRecent() {
  searchStore.set({ recent: [] });
  saveRecent([]);
}

export const useSearchOpen = () => useStore(searchStore, (s) => s.open);
export const useSearchAnchor = () => useStore(searchStore, (s) => s.anchor);
export const useRecentMarkets = () => useStore(searchStore, (s) => s.recent);
